/**
 * Rechnungs-Dokumentmodell (WS-18): Vorschau und PDF nutzen dasselbe Modell; was gedruckt
 * wird, entscheidet allein diese Funktion. Sprache nach WS-03. Dazu Bankdaten-Prüfung (ST-20),
 * Dateiname (NR-14) und die Pflichtangaben-Prüfung beim Anfordern (ST-21, KA-18).
 */
import { normalizeCountry, zoneOfCountry } from "./countries.js";
import { checkCurrencyAllowed, computeInvoiceTotals, formatMoney, isCurrency, moneyLocaleFor, type LineInput } from "./money.js";
import {
  DEFAULT_TAX_CONFIG,
  determineTaxTreatment,
  F,
  renderTaxNotice,
  taxIdLabel,
  taxIdRequirement,
  type TaxConfig,
  type TaxTreatment,
} from "./tax.js";
import type { Cents, CountryCode, Currency, Finding, IsoDate, Language, MoneyLocale } from "./types.js";

// ---------------------------------------------------------------------------
// Sprache (WS-03)
// ---------------------------------------------------------------------------

/**
 * EINE Funktion für die Rechnungssprache: ausdrückliche Empfängersprache → eigene Firma
 * (Agentur-Einstellung) → „de“ für DE/AT/CH → sonst „en“.
 */
export function resolveInvoiceLanguage(input: {
  explicitLanguage?: Language | null;
  recipientCountry: string | null;
  recipientIsOwnOrg?: boolean;
  ownOrgLanguage?: Language;
}): Language {
  if (input.explicitLanguage) return input.explicitLanguage;
  if (input.recipientIsOwnOrg && input.ownOrgLanguage) return input.ownOrgLanguage;
  const c = normalizeCountry(input.recipientCountry);
  return c === "DE" || c === "AT" || c === "CH" ? "de" : "en";
}

// ---------------------------------------------------------------------------
// Bankdaten (ST-20)
// ---------------------------------------------------------------------------

/** IBAN nach ISO 13616 (mod 97). */
export function isValidIban(iban: string): boolean {
  const s = iban.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s)) return false;
  const rearranged = s.slice(4) + s.slice(0, 4);
  let rem = 0;
  for (const ch of rearranged) {
    const v = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of v) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem === 1;
}

/** BIC nach SWIFT-Muster (8 oder 11 Zeichen). */
export function isValidBic(bic: string): boolean {
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic.replace(/\s+/g, "").toUpperCase());
}

export interface BankDetails {
  accountHolder?: string | null;
  bankName?: string | null;
  bankCountry?: string | null;
  iban?: string | null;
  bic?: string | null;
  /** Zahlungsweg (0055): SEPA/SWIFT mit Konto, PayPal oder Wise mit Adresse; ohne Angabe Überweisung */
  method?: "sepa" | "swift" | "paypal" | "wise" | null;
  /** Kontonummer außerhalb von IBAN (SWIFT) */
  accountNumber?: string | null;
  /** Bankleitzahl, Routing- oder Sort-Code (SWIFT) */
  routingCode?: string | null;
  /** Adresse für PayPal bzw. Wise */
  email?: string | null;
}

export function checkBankDetails(bank: BankDetails | null | undefined): Finding[] {
  const out: Finding[] = [];
  const b = bank ?? {};
  for (const f of ["accountHolder", "bankName", "bankCountry", "iban", "bic"] as const) {
    if (!b[f] || !String(b[f]).trim()) out.push({ code: "BANK_FIELD_MISSING", severity: "error", params: { field: f } });
  }
  if (b.iban && b.iban.trim() && !isValidIban(b.iban)) out.push({ code: "IBAN_INVALID", severity: "error" });
  if (b.bic && b.bic.trim() && !isValidBic(b.bic)) out.push({ code: "BIC_INVALID", severity: "error" });
  return out;
}

// ---------------------------------------------------------------------------
// Dateiname (NR-14)
// ---------------------------------------------------------------------------

function transliterate(s: string): string {
  return s
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/Ä/g, "Ae").replace(/Ö/g, "Oe").replace(/Ü/g, "Ue").replace(/ß/g, "ss")
    .normalize("NFD").replace(/\p{M}/gu, "")
    .replace(/[^A-Za-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 44)
    .replace(/-$/g, "");
}

export function invoiceFileName(input: { number: string; issuerName: string; recipientName: string; language: Language; creditNote?: boolean; selfBilling?: boolean }): string {
  // 0345: Stornobeleg zur Gutschrift
  const prefix = input.creditNote && input.selfBilling ? (input.language === "en" ? "Self-billing-cancellation" : "Storno-Gutschrift")
    : input.creditNote ? (input.language === "en" ? "Credit-note" : "Stornorechnung")
    : input.selfBilling ? (input.language === "en" ? "Self-billing-invoice" : "Gutschrift") : input.language === "en" ? "Invoice" : "Rechnung";
  return `${prefix}_${transliterate(input.number)}_${transliterate(input.issuerName)}_${transliterate(input.recipientName)}.pdf`;
}

// ---------------------------------------------------------------------------
// Dokumentmodell
// ---------------------------------------------------------------------------

export interface PartyInput {
  name: string;
  addressLines: string[];
  country: string | null;
  vatId?: string | null;
  taxNumber?: string | null;
}

export interface InvoiceDocumentInput {
  number: string;
  invoiceDate: IsoDate;
  serviceDate?: IsoDate | null;
  servicePeriod?: { from: IsoDate; to: IsoDate } | null;
  dueDate: IsoDate | null;
  currency: Currency;
  issuer: PartyInput & { smallBusiness: boolean; bank?: BankDetails | null };
  recipient: PartyInput & { isBusiness: boolean; language?: Language | null; isOwnOrg?: boolean };
  lines: readonly LineInput[];
  /** Talent-Rechnung an die Marke nach Abzug der Agenturprovision (KZ-05) */
  commissionPercent?: number;
  ownOrgLanguage?: Language;
  taxConfig?: TaxConfig;
  noticeOverrides?: Partial<Record<string, Partial<Record<Language, string>>>>;
}

export interface PrintedField {
  key: string;
  label: string;
  value: string;
}

export interface InvoiceDocument {
  language: Language;
  locale: MoneyLocale;
  titleKey: "invoice.title";
  number: string;
  invoiceDate: IsoDate;
  service: { from: IsoDate; to: IsoDate } | null;
  dueDate: IsoDate | null;
  issuer: { name: string; addressLines: string[]; fields: PrintedField[] };
  recipient: { name: string; addressLines: string[]; fields: PrintedField[] };
  lines: { description: string; quantity: number; unitPriceCents: Cents; totalCents: Cents; unitPrice: string; total: string }[];
  totals: { netCents: Cents; vatCents: Cents; grossCents: Cents; ratePercent: number; net: string; vat: string; gross: string };
  tax: TaxTreatment;
  taxNotice: { key: string; text: string };
  qrBill: boolean;
  bank: BankDetails | null;
  fileName: string;
  findings: Finding[];
}

export function buildInvoiceDocument(input: InvoiceDocumentInput): InvoiceDocument {
  const cfg = input.taxConfig ?? DEFAULT_TAX_CONFIG;
  const findings: Finding[] = [];
  const recipientCountry = normalizeCountry(input.recipient.country);
  const language = resolveInvoiceLanguage({
    explicitLanguage: input.recipient.language ?? null,
    recipientCountry,
    recipientIsOwnOrg: input.recipient.isOwnOrg ?? false,
    ...(input.ownOrgLanguage ? { ownOrgLanguage: input.ownOrgLanguage } : {}),
  });
  const locale = moneyLocaleFor(language, normalizeCountry(input.issuer.country));

  const taxInput = (grossCents: Cents) => ({
    issuerCountry: input.issuer.country,
    issuerSmallBusiness: input.issuer.smallBusiness,
    issuerVatId: input.issuer.vatId ?? null,
    issuerTaxNumber: input.issuer.taxNumber ?? null,
    recipientCountry: input.recipient.country,
    recipientIsBusiness: input.recipient.isBusiness,
    recipientVatId: input.recipient.vatId ?? null,
    currency: input.currency,
    grossCents,
  });
  // zwei Durchläufe: Satz bestimmen, dann mit dem echten Brutto (Kleinbetragsgrenze) erneut
  const net0 = computeInvoiceTotals(input.lines, { vatRatePercent: 0, ...(input.commissionPercent !== undefined ? { commissionPercent: input.commissionPercent } : {}) });
  const first = determineTaxTreatment(taxInput(net0.netCents), cfg);
  const totals1 = computeInvoiceTotals(input.lines, { vatRatePercent: first.ratePercent, ...(input.commissionPercent !== undefined ? { commissionPercent: input.commissionPercent } : {}) });
  const tax = determineTaxTreatment(taxInput(totals1.grossCents), cfg);
  const totals = tax.ratePercent === first.ratePercent ? totals1 : computeInvoiceTotals(input.lines, { vatRatePercent: tax.ratePercent, ...(input.commissionPercent !== undefined ? { commissionPercent: input.commissionPercent } : {}) });
  findings.push(...tax.findings);
  for (const i of net0.omitted) findings.push({ code: "LINE_OMITTED", severity: "warning", params: { line: i + 1 } });

  // ST-10/WS-18: USt-IdNr. des Ausstellers nicht bei Kleinunternehmer im Inland; Steuernummer immer, wenn vorhanden
  const issuerFields: PrintedField[] = [];
  // Praxistest K-132 (14.10.2026): Fehlt die Steuernummer, steht auch beim Kleinunternehmer die USt-IdNr. auf dem Beleg. Eine der
  // beiden Angaben ist auf jeder Rechnung Pflicht; ohne diese Ausnahme stand bei „Kleinunternehmer“ gar keine Kennung im Kopf.
  const printIssuerVat = !!input.issuer.vatId?.trim() && (tax.mode !== "SMALL_BUSINESS" || !input.issuer.taxNumber?.trim());
  if (input.issuer.taxNumber?.trim()) {
    issuerFields.push({ key: "taxNumber", label: language === "de" ? "Steuernummer" : "Tax number", value: input.issuer.taxNumber.trim() });
  }
  if (printIssuerVat) issuerFields.push({ key: "vatId", label: taxIdLabel(input.issuer.country, language), value: input.issuer.vatId!.trim() });
  const recipientFields: PrintedField[] = [];
  if (input.recipient.vatId?.trim()) {
    recipientFields.push({ key: "vatId", label: taxIdLabel(input.recipient.country, language), value: input.recipient.vatId.trim() });
  }

  const bankFindings = checkBankDetails(input.issuer.bank);
  findings.push(...bankFindings);

  const service = input.servicePeriod ?? (input.serviceDate ? { from: input.serviceDate, to: input.serviceDate } : null);
  if (!service && !tax.smallAmountInvoice) findings.push({ code: "SERVICE_DATE_REQUIRED", severity: "error" });

  const fmt = (c: Cents) => formatMoney(c, input.currency, locale);
  return {
    language,
    locale,
    titleKey: "invoice.title",
    number: input.number,
    invoiceDate: input.invoiceDate,
    service,
    dueDate: input.dueDate,
    issuer: { name: input.issuer.name, addressLines: [...input.issuer.addressLines], fields: issuerFields },
    recipient: { name: input.recipient.name, addressLines: [...input.recipient.addressLines], fields: recipientFields },
    lines: totals.lines.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unitPriceCents: l.unitPriceCents,
      totalCents: l.lineTotalCents,
      unitPrice: fmt(l.unitPriceCents),
      total: fmt(l.lineTotalCents),
    })),
    totals: {
      netCents: totals.netCents,
      vatCents: totals.vatCents,
      grossCents: totals.grossCents,
      ratePercent: tax.ratePercent,
      net: fmt(totals.netCents),
      vat: fmt(totals.vatCents),
      gross: fmt(totals.grossCents),
    },
    tax,
    taxNotice: { key: tax.noticeKey, text: renderTaxNotice(tax.noticeKey, language, { rate: tax.ratePercent }, input.noticeOverrides) },
    qrBill: tax.requiredFields.includes(F.chQrIban),
    bank: input.issuer.bank ?? null,
    fileName: invoiceFileName({ number: input.number, issuerName: input.issuer.name, recipientName: input.recipient.name, language }),
    findings,
  };
}

// ---------------------------------------------------------------------------
// Pflichtangaben beim Anfordern (ST-21, KA-18)
// ---------------------------------------------------------------------------

export type RequestCheckBucket = "required" | "important" | "hint";

export type RequestCheckCode =
  | "RECIPIENT_LEGAL_NAME"
  | "RECIPIENT_ADDRESS"
  | "RECIPIENT_VAT_ID"
  | "ISSUER_TAX_ID"
  | "ISSUER_VAT_ID"
  | "ISSUER_IBAN"
  | "ISSUER_ADDRESS"
  | "ISSUER_ABROAD"
  | "AMOUNT"
  | "CURRENCY"
  | "NOT_VISIBLE_IN_PORTAL"
  | "NO_PORTAL_ACCOUNT";

/** Welcher Befund in welchen Topf kommt – Einstellung der Agentur. */
export const DEFAULT_REQUEST_CHECK_BUCKETS: Readonly<Record<RequestCheckCode, RequestCheckBucket>> = {
  RECIPIENT_LEGAL_NAME: "required",
  RECIPIENT_ADDRESS: "required",
  RECIPIENT_VAT_ID: "required",
  ISSUER_TAX_ID: "required",
  ISSUER_VAT_ID: "required",
  ISSUER_IBAN: "required",
  ISSUER_ADDRESS: "important",
  ISSUER_ABROAD: "hint",
  AMOUNT: "required",
  CURRENCY: "required",
  NOT_VISIBLE_IN_PORTAL: "important",
  NO_PORTAL_ACCOUNT: "important",
};

export interface RequestCheckInput {
  recipient: { legalName?: string | null; addressLines?: string[] | null; country: string | null; vatId?: string | null };
  issuer: {
    country: string | null;
    smallBusiness: boolean;
    vatId?: string | null;
    taxNumber?: string | null;
    iban?: string | null;
    addressLines?: string[] | null;
  };
  orgCountry: CountryCode;
  amountCents: Cents | null;
  commissionOnly: boolean;
  currency: string;
  allowedCurrencies: readonly Currency[];
  visibleInPortal: boolean;
  hasPortalAccount: boolean;
}

function addressHasMoreThanCountry(lines: string[] | null | undefined): boolean {
  return (lines ?? []).map((l) => l.trim()).filter(Boolean).some((l) => !normalizeCountry(l));
}

/**
 * Prüfung beim Anfordern einer Rechnung: drei Töpfe, blockiert NIE (KA-18). Die Steuernummer-
 * Pflicht kommt aus derselben Funktion wie im Generator (WS-07).
 */
export function checkInvoiceRequest(
  input: RequestCheckInput,
  buckets: Readonly<Record<RequestCheckCode, RequestCheckBucket>> = DEFAULT_REQUEST_CHECK_BUCKETS,
  taxConfig: TaxConfig = DEFAULT_TAX_CONFIG,
): { required: Finding[]; important: Finding[]; hints: Finding[]; blocking: false } {
  const codes: RequestCheckCode[] = [];
  if (!input.recipient.legalName?.trim()) codes.push("RECIPIENT_LEGAL_NAME");
  if (!addressHasMoreThanCountry(input.recipient.addressLines)) codes.push("RECIPIENT_ADDRESS");

  // Unbekannte Währung: kein stiller Rückfall auf EUR, die Prüfung meldet sie unten als CURRENCY
  const currency = isCurrency(input.currency) ? input.currency : null;
  const req = taxIdRequirement(
    {
      issuerCountry: input.issuer.country,
      issuerSmallBusiness: input.issuer.smallBusiness,
      recipientCountry: input.recipient.country,
      grossCents: input.amountCents ?? Number.MAX_SAFE_INTEGER,
      currency,
    },
    taxConfig,
  );
  const has = (v?: string | null) => !!v && v.trim() !== "";
  if (req.fields.includes(F.recipientVatId) && !has(input.recipient.vatId)) codes.push("RECIPIENT_VAT_ID");
  if (req.fields.includes(F.issuerTaxNumberOrVatId) && !has(input.issuer.vatId) && !has(input.issuer.taxNumber)) codes.push("ISSUER_TAX_ID");
  if ((req.fields.includes(F.issuerVatId) || req.fields.includes(F.issuerChUid)) && !has(input.issuer.vatId)) codes.push("ISSUER_VAT_ID");
  if (!has(input.issuer.iban)) codes.push("ISSUER_IBAN");
  if (!addressHasMoreThanCountry(input.issuer.addressLines)) codes.push("ISSUER_ADDRESS");
  const ic = normalizeCountry(input.issuer.country);
  if (ic && ic !== input.orgCountry) codes.push("ISSUER_ABROAD");
  if (!input.commissionOnly && !(input.amountCents && input.amountCents > 0)) codes.push("AMOUNT");
  if (checkCurrencyAllowed(input.currency, input.allowedCurrencies)) codes.push("CURRENCY");
  if (!input.visibleInPortal) codes.push("NOT_VISIBLE_IN_PORTAL");
  if (!input.hasPortalAccount) codes.push("NO_PORTAL_ACCOUNT");
  if (zoneOfCountry(input.recipient.country) === "UNKNOWN" && !codes.includes("RECIPIENT_ADDRESS")) codes.push("RECIPIENT_ADDRESS");

  const out = { required: [] as Finding[], important: [] as Finding[], hints: [] as Finding[], blocking: false as const };
  for (const c of codes) {
    const b = buckets[c];
    const f: Finding = { code: c, severity: b === "required" ? "error" : b === "important" ? "warning" : "hint" };
    (b === "required" ? out.required : b === "important" ? out.important : out.hints).push(f);
  }
  return out;
}
